/**
 * Configuration for the 5 organization "lookup" resources that an employee
 * record FKs to (department, branch, designation, employment-type, grade).
 *
 * Used by <QuickAddSelect>, <QuickAddResourceDialog>, <ManageResourceDialog>
 * in `org-resource-pickers.tsx` so they can render generic forms / tables
 * driven by config instead of one component per resource.
 */

export type OrgResourceKind =
  | "department"
  | "branch"
  | "designation"
  | "employment_type"
  | "grade";

export interface QuickAddFieldDef {
  name: string;
  label: string;
  type: "text" | "select";
  required?: boolean;
  placeholder?: string;
  helper?: string;
  options?: ReadonlyArray<{ value: string; label: string }>;
  defaultValue?: string;
  /** Render as monospace (codes / IDs). */
  mono?: boolean;
}

export interface OrgResourceConfig {
  kind: OrgResourceKind;
  /** API endpoint relative to /api/v1, no trailing slash needed when appending id. */
  endpoint: string;
  singular: string;
  plural: string;
  /** Fields shown in the quick-add modal. Each is sent to POST verbatim. */
  quickAddFields: ReadonlyArray<QuickAddFieldDef>;
  /** Columns shown in the manage modal table. */
  tableColumns: ReadonlyArray<{ key: string; label: string }>;
}

/** PSA Standard Geographic Code regions — mirrors `apps.organization.enums.PHRegion`. */
export const PH_REGIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "NCR", label: "NCR — National Capital Region" },
  { value: "CAR", label: "CAR — Cordillera" },
  { value: "R1", label: "Region I — Ilocos" },
  { value: "R2", label: "Region II — Cagayan Valley" },
  { value: "R3", label: "Region III — Central Luzon" },
  { value: "R4A", label: "Region IV-A — CALABARZON" },
  { value: "MIMAROPA", label: "Region IV-B — MIMAROPA" },
  { value: "R5", label: "Region V — Bicol" },
  { value: "R6", label: "Region VI — Western Visayas" },
  { value: "R7", label: "Region VII — Central Visayas" },
  { value: "R8", label: "Region VIII — Eastern Visayas" },
  { value: "R9", label: "Region IX — Zamboanga Peninsula" },
  { value: "R10", label: "Region X — Northern Mindanao" },
  { value: "R11", label: "Region XI — Davao" },
  { value: "R12", label: "Region XII — SOCCSKSARGEN" },
  { value: "R13", label: "Region XIII — Caraga" },
  { value: "BARMM", label: "BARMM" },
];

/** PH Labor Code Art. 295/296 categories — mirrors `EmploymentTypeCode`. */
export const EMPLOYMENT_TYPE_CODES: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "regular", label: "Regular" },
  { value: "probationary", label: "Probationary" },
  { value: "fixed_term", label: "Fixed-term" },
  { value: "project_based", label: "Project-based" },
  { value: "seasonal", label: "Seasonal" },
  { value: "casual", label: "Casual" },
];

export const SALARY_MODES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "monthly", label: "Monthly" },
  { value: "semi_monthly", label: "Semi-monthly" },
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "hourly", label: "Hourly" },
];

export const ORG_RESOURCES: Record<OrgResourceKind, OrgResourceConfig> = {
  department: {
    kind: "department",
    endpoint: "/organization/departments/",
    singular: "department",
    plural: "departments",
    quickAddFields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
        placeholder: "e.g. Human Resources",
      },
      {
        name: "code",
        label: "Code",
        type: "text",
        placeholder: "Optional (e.g. HR)",
        mono: true,
      },
    ],
    tableColumns: [
      { key: "name", label: "Name" },
      { key: "code", label: "Code" },
    ],
  },
  branch: {
    kind: "branch",
    endpoint: "/organization/branches/",
    singular: "branch",
    plural: "branches",
    quickAddFields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
        placeholder: "e.g. Cebu Head Office",
      },
      {
        name: "code",
        label: "Code",
        type: "text",
        required: true,
        placeholder: "e.g. CEB-HO",
        mono: true,
      },
      {
        name: "region",
        label: "Region",
        type: "select",
        required: true,
        defaultValue: "NCR",
        options: PH_REGIONS,
      },
    ],
    tableColumns: [
      { key: "name", label: "Name" },
      { key: "code", label: "Code" },
      { key: "region", label: "Region" },
    ],
  },
  designation: {
    kind: "designation",
    endpoint: "/organization/designations/",
    singular: "designation",
    plural: "designations",
    quickAddFields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
        placeholder: "e.g. Software Engineer",
      },
    ],
    tableColumns: [{ key: "name", label: "Name" }],
  },
  employment_type: {
    kind: "employment_type",
    endpoint: "/organization/employment-types/",
    singular: "employment type",
    plural: "employment types",
    quickAddFields: [
      {
        name: "code",
        label: "Category",
        type: "select",
        required: true,
        defaultValue: "regular",
        options: EMPLOYMENT_TYPE_CODES,
        helper: "PH Labor Code Art. 295/296 category. Drives probation + statutory rules.",
      },
      {
        name: "name",
        label: "Display name",
        type: "text",
        required: true,
        placeholder: "e.g. Regular Employee",
      },
    ],
    tableColumns: [
      { key: "code", label: "Code" },
      { key: "name", label: "Name" },
    ],
  },
  grade: {
    kind: "grade",
    endpoint: "/organization/grades/",
    singular: "grade",
    plural: "grades",
    quickAddFields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
        placeholder: "e.g. G3 — Associate",
      },
      {
        name: "code",
        label: "Code",
        type: "text",
        placeholder: "Optional (e.g. G3)",
        mono: true,
      },
      {
        name: "salary_mode",
        label: "Salary mode",
        type: "select",
        defaultValue: "monthly",
        options: SALARY_MODES,
      },
    ],
    tableColumns: [
      { key: "name", label: "Name" },
      { key: "code", label: "Code" },
      { key: "salary_mode", label: "Mode" },
    ],
  },
};

/** TanStack-Query key prefix for any list query on a given resource. */
export function listQueryKey(kind: OrgResourceKind, companyId: string) {
  return [kindToPluralKey(kind), companyId] as const;
}

/** Stable plural cache key — matches the keys used in <CreateEmployeeDialog>. */
function kindToPluralKey(kind: OrgResourceKind): string {
  switch (kind) {
    case "department":
      return "departments";
    case "branch":
      return "branches";
    case "designation":
      return "designations";
    case "employment_type":
      return "employment-types";
    case "grade":
      return "grades";
  }
}

/** Tab key for /org-settings?tab=… deep links. */
export function kindToSettingsTab(kind: OrgResourceKind): string {
  // Same plural form is used as both the cache key and the URL tab slug.
  return kindToPluralKey(kind);
}
